import { useQuery } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { Button } from '@/components/ui/button';
import { userDetailQueryOptions } from '@/features/users/api/users.api';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { cn } from '@/lib/utils';
import {
  assignedInstitutesInfiniteQueryOptions,
  unassignedInstitutesInfiniteQueryOptions,
} from '../api/userInstituteMapping.api';
import { AssignedInstitutesTable } from '../components/AssignedInstitutesTable';
import { MappingFilterPanel } from '../components/MappingFilterPanel';
import { UnassignedInstitutesTable } from '../components/UnassignedInstitutesTable';
import { UserProfileCard } from '../components/UserProfileCard';
import { useUserMappingFilters, type ActiveTab } from '../hooks/useUserMappingFilters';

interface MappingTabContent {
  label: string;
  emptyTitle: string;
  errorTitle: string;
  userCardTitle: string;
}

const TAB_CONTENT: Record<ActiveTab, MappingTabContent> = {
  current: {
    label: 'Current Institute',
    emptyTitle: 'Select filters to view assigned institutes',
    errorTitle: 'Could not load assigned institutes',
    userCardTitle: 'Currently Assigned',
  },
  assign: {
    label: 'Assign',
    emptyTitle: 'Select filters to view unassigned institutes',
    errorTitle: 'Could not load unassigned institutes',
    userCardTitle: 'Assigning to',
  },
};

const TABS: readonly ActiveTab[] = ['current', 'assign'];

export function UserInstituteMappingPage() {
  const controller = useUserMappingFilters();
  const { activeTab, appliedUserId, institutesQueryParams, handleTabChange } = controller;

  const { data: appliedUser } = useQuery({
    ...userDetailQueryOptions(String(appliedUserId ?? 0)),
    enabled: appliedUserId !== undefined,
  });

  const assigned = useInfiniteTableQuery(
    assignedInstitutesInfiniteQueryOptions(institutesQueryParams),
  );
  const unassigned = useInfiniteTableQuery(
    unassignedInstitutesInfiniteQueryOptions(institutesQueryParams),
  );

  const tabContent = TAB_CONTENT[activeTab];
  const activeTabQuery = activeTab === 'current' ? assigned : unassigned;

  function renderAssignedInstitutes(userId: number) {
    if (!assigned.isLoading && assigned.rows.length === 0) {
      return (
        <EmptyState
          icon={Building2}
          title="This user has no assigned institutes"
          description="Switch to the Assign tab to add institutes for this user."
          fill
          actions={
            <Button variant="outline" size="sm" onClick={() => handleTabChange('assign')}>
              Go to Assign Tab
            </Button>
          }
        />
      );
    }

    return (
      <AssignedInstitutesTable
        data={assigned.rows}
        isLoading={assigned.isLoading}
        userId={userId}
        {...assigned.loadMoreProps}
      />
    );
  }

  function renderTabBody() {
    if (institutesQueryParams === null) {
      return (
        <EmptyState
          kind="pickFirst"
          title={tabContent.emptyTitle}
          description="Pick a ZM, a division and a district above."
          fill
        />
      );
    }

    if (activeTabQuery.loadError) {
      return (
        <ErrorState
          error={activeTabQuery.loadError}
          fallbackTitle={tabContent.errorTitle}
          onRetry={() => void activeTabQuery.refetch()}
          fill
        />
      );
    }

    if (!appliedUser) return null;

    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <div className="sticky top-app-header z-30 rounded-xl border bg-card p-4 shadow-sm">
          <p className="mb-3 text-base font-semibold">{tabContent.userCardTitle}</p>
          <UserProfileCard user={appliedUser} />
        </div>

        {activeTab === 'current' ? (
          renderAssignedInstitutes(institutesQueryParams.userId)
        ) : (
          <UnassignedInstitutesTable
            key={JSON.stringify(institutesQueryParams)}
            data={unassigned.rows}
            isLoading={unassigned.isLoading}
            userId={institutesQueryParams.userId}
            {...unassigned.loadMoreProps}
          />
        )}
      </div>
    );
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Institute With User Mapping"
        actions={
          <div className="inline-flex w-full items-center rounded-lg border bg-muted/40 p-1 sm:w-auto">
            {TABS.map((tab) => (
              <Button
                key={tab}
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleTabChange(tab)}
                className={cn(
                  'h-8 flex-1 px-4 sm:flex-none',
                  activeTab === tab
                    ? 'bg-card text-foreground shadow-sm hover:bg-card'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {TAB_CONTENT[tab].label}
              </Button>
            ))}
          </div>
        }
      />

      <MappingFilterPanel filters={controller} selectedUser={appliedUser} />

      {renderTabBody()}
    </div>
  );
}
